import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { delivererIcon, restaurantIcon, FitBounds } from "@/components/delivery/deliveryMapIcons";
import DeliveryMapSidebar from "@/components/delivery/DeliveryMapSidebar";

const API_WS_BASE = (process.env.REACT_APP_BACKEND_URL || "https://api.kaisosystem.com")
  .replace(/^http/, "ws");

export default function DeliveryMapPage() {
  const { authAxios, user } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const wsRef = useRef(null);
  const [positions, setPositions] = useState([]);
  const [activeDeliveries, setActiveDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pedidos");
  const [selectedDeliverer, setSelectedDeliverer] = useState(null);
  const [routeHistory, setRouteHistory] = useState(null);
  const [showDispatchSettings, setShowDispatchSettings] = useState(false);
  const [dispatchSettings, setDispatchSettings] = useState({
    auto_dispatch_enabled: false,
    prioritize_closest: true,
    prioritize_rating: false,
    max_distance_km: 10,
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const center = [37.8882, -4.7794];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    try {
      const [posRes, delRes] = await Promise.all([
        authAxiosRef.current.get("/tracking/positions"),
        authAxiosRef.current.get("/tracking/active-deliveries")
      ]);
      setPositions(posRes.data);
      setActiveDeliveries(delRes.data);
    } catch (err) { console.error("Error fetching map data"); }
    setLoading(false);
  }, []);

  useEffect(() => {
    const rid = user?.restaurant_id;
    if (!rid) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    let ws;
    let retryTimeout;
    const connect = () => {
      ws = new WebSocket(`${API_WS_BASE}/api/ws/kds/${rid}?token=${token}`);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          const t = msg.event || msg.type;
          if (["order_auto_assigned", "order_updated", "order_waiting_driver"].includes(t)) {
            fetchData();
            if (t === "order_auto_assigned") toast.success(`Despacho automático: ${msg.data?.driver_name || "estafeta"} atribuído!`);
            if (t === "order_waiting_driver") toast.warning("Pedido pronto sem estafeta disponível");
          }
        } catch {}
      };
      ws.onclose = () => { retryTimeout = setTimeout(connect, 5000); };
    };
    connect();
    return () => { clearTimeout(retryTimeout); ws?.close(); };
  }, [user?.restaurant_id, fetchData]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    authAxiosRef.current.get("/dispatch/settings").then(res => setDispatchSettings(res.data)).catch(() => {});
  }, []);

  const saveDispatchSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await authAxios.put("/dispatch/settings", dispatchSettings);
      setDispatchSettings(res.data.settings);
      toast.success(dispatchSettings.auto_dispatch_enabled ? "Despacho automático ativado!" : "Configurações salvas");
      setShowDispatchSettings(false);
    } catch { toast.error("Erro ao salvar"); }
    setSavingSettings(false);
  };

  const loadRouteHistory = async (delivererId) => {
    try {
      const res = await authAxios.get(`/tracking/route/${delivererId}`);
      setRouteHistory(res.data);
      setSelectedDeliverer(delivererId);
    } catch { toast.error("Error cargando ruta"); }
  };

  const simulateGPS = async (delivererId) => {
    try {
      await authAxios.post(`/tracking/simulate/${delivererId}`);
      toast.success("Simulación GPS ejecutada");
      fetchData();
      loadRouteHistory(delivererId);
    } catch { toast.error("Error simulando"); }
  };

  if (loading) return <LoadingScreen />;

  const routePoints = routeHistory?.points?.filter(p => p.lat && p.lng) || [];
  const routePolyline = routePoints.map(p => [p.lat, p.lng]);
  const allMapPositions = [
    ...positions.filter(p => p.lat && p.lng),
    ...(routePoints.length > 0 ? routePoints : [])
  ];

  return (
    <div className="h-screen flex" data-testid="delivery-map-page">
      <DeliveryMapSidebar
        positions={positions}
        activeDeliveries={activeDeliveries}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        selectedDeliverer={selectedDeliverer}
        setSelectedDeliverer={setSelectedDeliverer}
        routeHistory={routeHistory}
        setRouteHistory={setRouteHistory}
        dispatchSettings={dispatchSettings}
        setDispatchSettings={setDispatchSettings}
        showDispatchSettings={showDispatchSettings}
        setShowDispatchSettings={setShowDispatchSettings}
        savingSettings={savingSettings}
        saveDispatchSettings={saveDispatchSettings}
        loadRouteHistory={loadRouteHistory}
        simulateGPS={simulateGPS}
        fetchData={fetchData}
        routePoints={routePoints}
      />
      <div className="flex-1 relative" data-testid="delivery-map-container">
        <MapContainer center={center} zoom={13} className="h-full w-full" zoomControl={true}>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Marker position={center} icon={restaurantIcon}><Popup><strong>Kaiso Sushi</strong><br />Restaurante</Popup></Marker>
          {positions.filter(d => d.lat && d.lng).map(d => (
            <Marker key={d.id} position={[d.lat, d.lng]} icon={delivererIcon(d.vehicle)} eventHandlers={{ click: () => loadRouteHistory(d.id) }}>
              <Popup><div className="text-sm"><strong>{d.name}</strong> ({d.vehicle})<br /><span className="text-gray-500">Status: {d.status}</span>{d.current_order && (<><br /><span className="text-emerald-600 font-bold">Pedido #{d.current_order.id.slice(-6).toUpperCase()}</span></>)}</div></Popup>
            </Marker>
          ))}
          {routePolyline.length > 1 && <Polyline positions={routePolyline} pathOptions={{ color: "#3b82f6", weight: 3, opacity: 0.8 }} />}
          {routePoints.map((point, idx) => (
            <Marker key={point.id} position={[point.lat, point.lng]} icon={L.divIcon({ className: "route-point", html: `<div style="background:#3b82f6;width:20px;height:20px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3);font-size:9px;color:white;font-weight:bold;">${idx + 1}</div>`, iconSize: [20, 20], iconAnchor: [10, 10] })}>
              <Popup><div className="text-xs"><strong>Punto {idx + 1}</strong><br />{new Date(point.timestamp).toLocaleTimeString("es-ES")}<br />{point.lat.toFixed(6)}, {point.lng.toFixed(6)}</div></Popup>
            </Marker>
          ))}
          {allMapPositions.length > 0 && <FitBounds positions={allMapPositions} />}
        </MapContainer>
      </div>
    </div>
  );
}
